import React, { useEffect, useState } from 'react';
import { Box, Divider, HStack, VStack } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import SelectLoyalty from '../dropdowns/SelectLoyalty';
import {
    useSpringBigStore,
    usePointsToCashGiftCard,
    observer,
    GiftCard,
    i18nFn,
    GIFTCARD_FLOWS
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';
import { useToast } from '~/hooks/toast';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';

const PointsConverter = ({ t }: { t: i18nFn }) => {

    const { member, merchant, giftCards } = useSpringBigStore();

    const [convertPoints, setConvertPoints] = React.useState(0);
    const [pointsError, setPointsError] = React.useState('');
    const [loyaltyCardError, setLoyaltyCardError] = React.useState('');
    const [submitError, setSubmitError] = React.useState('');
    const [loyaltyCard, setLoyaltyCard] = React.useState<GiftCard | null>(null);

    const gStyle = useGeneralStyles();
    const { successToast } = useToast();

    const dallarPerPoint = parseFloat(merchant?.dollars_per_point || '0.0');
    const pointToDollar = convertPoints / dallarPerPoint;
    const [isConfirmState, setIsConfirmState] = useState(false);

    const {
        mutate: convertPointsToDollar,
        isLoading,
        error: pointToCashError
    } = usePointsToCashGiftCard();

    const isPointsToDollarDiabled = pointToDollar === 0 || isLoading || pointsError !== '' || !loyaltyCard;

    useEffect(() => {

        pointToCashError && setSubmitError(t('general.submitError'));
    }, [pointToCashError]);

    const onAddPointsToCash = () => {

        const cardNumber = loyaltyCard?.card_number;

        if (!cardNumber) {
            setLoyaltyCardError(t('giftCard.pointsConvertor.selectRoyaltyError'));
        }

        if (cardNumber && member?.id) {

            convertPointsToDollar({
                member_id: member?.id.toString() || '',
                card_number: cardNumber,
                convert_points: convertPoints
            },
            {
                onSuccess: (_) => {

                    successToast(t('giftCard.pointsConvertor.reloadSuccess'));
                    observer.emit('close-actionsheet');
                }
            });
        }
    };

    useEffect(() => {

        if (giftCards && !giftCards.length) {
            observer.emit('open-actionsheet', GIFTCARD_FLOWS.addFunds);
        }
    }, [giftCards]);
    
    if (isConfirmState) {
        return ( <VStack width="100%" alignItems="center">
          <Caption fontSize={24}>{t('giftCard.confirm_points.confirm_conversion')}</Caption>
          <Caption textAlign={"center"} py={6}>
            {t('giftCard.confirm_points.review_the_redemption')}
          </Caption>
          <VStack space={3} mb={6} w={"100%"}>
            <Box borderWidth={1} borderRadius="md" borderColor={gStyle.button_background} p={4}>
              <HStack justifyContent="space-between" mb={3}>
                <Caption>
                    {t('giftCard.confirm_points.total_points')}
                </Caption>
                <Caption>{convertPoints}</Caption>
              </HStack>
              <Divider />
              <HStack justifyContent="space-between">
                <Caption fontWeight="bold">
                    {t('giftCard.confirm_points.total_load_amount')}
                </Caption>
                <Caption fontWeight="bold" color={gStyle.button_background}>
                    ${pointToDollar.toFixed(2)}
                </Caption>
              </HStack>
            </Box>
            <VStack space={3} mb={6}>
              <Caption fontSize="sm" color="gray.500" textAlign={'center'}>
                {t('giftCard.confirm_points.payment_method')}
                <VStack px={2}>
                    <Caption fontWeight="bold" textAlign={'center'}>
                        {t('giftCard.confirm_points.points_conversion')}
                    </Caption>
                    {merchant?.dollars_per_point && <Caption textAlign={'center'}>
                        {t('giftCard.confirm_points.points_per', [1 / parseInt(merchant?.dollars_per_point)])}
                    </Caption>}
                </VStack>
              </Caption>
            </VStack>
            <Button
              size="lg"
              borderRadius="md"
              bg={gStyle.button_background}
              _text={{ color: gStyle.button_text }}
              onPress={onAddPointsToCash}
            >
                {t('giftCard.confirm_points.complete_conversion')}
            </Button>
          </VStack>
        </VStack>
      );      
    }

    return (
        <VStack space={5}>
            <Caption alignSelf='center' fontSize={24}>
                { t('giftCard.fund_with_points', [merchant?.gift_card_alias]) }
            </Caption>
            <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
            <VStack>
                <Caption fontSize={18} fontWeight={'300'}>
                    { t('giftCard.select_card' ,[merchant?.gift_card_alias]) }
                </Caption>
                <SelectLoyalty
                    loyaltyCard={loyaltyCard}
                    setLoyaltyCard={setLoyaltyCard}
                />
            </VStack>
            { loyaltyCardError &&
                <CaptionError>{ loyaltyCardError }</CaptionError>
            }
            { pointToCashError &&
                <CaptionError>{ (pointToCashError as any)?.data.message }</CaptionError>
            }
            <Button
                onPress={() => setIsConfirmState(true)}
                backgroundColor={gStyle.button_background}
                w='55%'
                alignSelf={'center'}
                borderRadius={10}
                isDisabled={isPointsToDollarDiabled}
            >
                { isLoading ?
                    <ButtonSpinner />
                    :
                    <Caption color={gStyle.button_text} fontSize={20}>
                        { t('general.add') } ${pointToDollar.toFixed(2)}
                    </Caption>
                }
            </Button>
        </VStack>
    );
};

export default PointsConverter;
